import type {
  IconDefinition,
  IconName,
  IconPrefix,
} from '@fortawesome/fontawesome-svg-core';
import type { IOmit, ISuggestProps } from '@sixui/ui';
import { forwardRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IFilterableListItem,
  ListItem,
  Suggest,
  useControlledValue,
} from '@sixui/ui';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';

import type {
  IFontAwesomeIconFamily,
  IFontAwesomeIconStyle,
  IFontAwesomeSearchIconResponseBody,
} from '~/hooks/useFontAwesomeSearchIconsQuery';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { useFontAwesomeSearchIconsQuery } from '~/hooks/useFontAwesomeSearchIconsQuery';
import { useFontAwesomeSessionTokenQuery } from '~/lib/api/modules/Providers/hooks/useFontAwesomeSessionTokenQuery';

const DEBOUNCE_DELAY = 300;

export type IFontAwesomeIconSelectorProps = IOmit<ISuggestProps, 'items'> & {
  query?: string;
  defaultQuery?: string;
  family: IFontAwesomeIconFamily;
  style: IFontAwesomeIconStyle;
  limit?: number;
};

const makeIconDefinition = (
  icon: IFontAwesomeSearchIconResponseBody,
): IconDefinition | undefined => {
  const svg = icon.svgs?.[0];
  if (!svg) {
    return undefined;
  }

  // For a monotone icon, the path used in an IconDefinition is just a string.
  // For a duotone icon, it's a list, with the secondary path appearing first.
  // The pathData field selected from the GraphQL API is always a list.

  const path = svg.pathData?.length == 1 ? svg.pathData?.[0] : svg.pathData;
  if (!path) {
    return undefined;
  }

  // If using `width` and `height` from an `IconUpload`, those types are
  // `String` and must be parsed as numbers when using in an `IconDefinition`.
  // The svgs field returns integers for width and height. This is one way to
  // ensure they are parsed as numbers, in some context where you might get
  // properties for either.

  if (!svg.width) {
    return undefined;
  }

  if (!svg.height) {
    return undefined;
  }

  // The unicode in an IconDefinition is always the hexadecimal string
  // representation.
  if (!icon.unicode) {
    return undefined;
  }

  if (!icon.label || !svg.familyStyle?.prefix) {
    return undefined;
  }

  const iconDefinition: IconDefinition = {
    prefix: svg.familyStyle.prefix as IconPrefix,
    iconName: icon.id as IconName,
    icon: [
      svg.width,
      svg.height,
      [], // no ligatures
      icon.unicode,
      path,
    ],
  };

  return iconDefinition;
};

export const FontAwesomeIconSelector = forwardRef<
  HTMLInputElement,
  IFontAwesomeIconSelectorProps
>(function FontAwesomeIconSelector(props, forwardedRef) {
  const {
    query: queryProp,
    defaultQuery,
    family,
    style,
    limit = 24,
    ...other
  } = props;
  const { t } = useTranslation();
  const fontAwesomeSessionTokenQuery = useFontAwesomeSessionTokenQuery();
  const fontAwesomeSessionToken =
    fontAwesomeSessionTokenQuery.data?.access_token;

  const [query, setQuery] = useControlledValue({
    controlled: queryProp,
    default: defaultQuery ?? '',
    name: 'FontAwesomeIconSelector',
  });
  const [items, setItems] = useState<Array<IFilterableListItem>>([]);

  const fontAwesomeSearchIconQuery = useFontAwesomeSearchIconsQuery(
    fontAwesomeSessionToken
      ? {
          accessToken: fontAwesomeSessionToken,
          query,
          family,
          style,
          limit,
        }
      : undefined,
    {
      onSuccess: (data) => {
        setItems(
          data.search
            ?.map((icon) => {
              const iconDefinition = makeIconDefinition(icon);
              const iconSvg = icon.svgs?.[0]?.html;

              return iconDefinition
                ? {
                    value: iconSvg ?? '',
                    label: icon.label,
                    icon: <FontAwesomeIcon icon={iconDefinition} />,
                  }
                : undefined;
            })
            .filter((item) => !!item),
        );
      },
    },
  );

  const handleQueryChange = (query: string): void => setQuery(query);

  const handleQueryChangeDebounced = useDebounceCallback(
    handleQueryChange,
    DEBOUNCE_DELAY,
  );

  return fontAwesomeSearchIconQuery.isError ? (
    <ErrorMessage error={fontAwesomeSearchIconQuery.error} />
  ) : (
    <Suggest
      {...other}
      onQueryChange={handleQueryChangeDebounced}
      items={items}
      ref={forwardedRef}
      loading={fontAwesomeSearchIconQuery.isFetching}
      noResults={
        <ListItem disabled>
          {fontAwesomeSearchIconQuery.isFetching
            ? t('common.searching')
            : t('common.noResults')}
        </ListItem>
      }
      placeholder={t('FontAwesomeIconSelector.placeholder')}
      cols={6}
      itemFocus="icon"
      listPredicate={undefined}
    />
  );
});
